import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { Popover, PopoverBody, PopoverCloseButton, PopoverFooter, PopoverHeader } from 'foundations-components/transitional/components/Popover';
import CoachingTipButton from './buttons/CoachingTipButton';
import CoachingTipSteps from './CoachingTipSteps';
import { FloatingFeedback } from './FloatingFeedback';
import useTipHandlers from '../hooks/useTipHandlers';
import { trackUserInteraction } from '../utils/usageTracker';
import { useTarget } from '../hooks/useTarget';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const BorderTopContainer = styled.div.withConfig({
  displayName: "CoachingTip__BorderTopContainer",
  componentId: "sc-5gisp6-0"
})(["border-top:", " solid ", ";inline-size:100%;"], ({
  theme
}) => theme.borderWidth[100], ({
  theme
}) => theme.color['border-core-default']);

// Placing coaching tips under the earth layer due to the menu on mobile devices being on earth layer.
// This fixes the issue where caoching tips renders on top of the menu
// Hardcoding the layer to 1000 as z-index token is not yet supported in Trellis
export const EARTH_LAYER = '1000';
const COACHING_TIPS_LAYER = parseInt(EARTH_LAYER, 10) - 1;
const CoachingTip = ({
  actionText,
  attachTo,
  beforeEnter,
  cta,
  dismissCoachingTips,
  fallbackElementQuery,
  coachingTipsId,
  isOpen,
  onTipRender,
  onTipUnmount,
  placement,
  popoverPlacement,
  requiresTargetDisappearAnimation,
  setCurrentTipId,
  steps,
  tipId,
  tipIndex,
  title,
  zIndex = COACHING_TIPS_LAYER,
  popoverZIndex = zIndex,
  feedbackSurveyId,
  isEmptyTarget = false
}) => {
  const {
    isTargetVisible,
    targetElement
  } = useTarget(attachTo, {
    requiresTargetDisappearAnimation,
    beforeEnter,
    fallbackElementQuery
  });
  const tipViewed = useRef(false);
  const {
    text: ctaText,
    href: ctaHref,
    handler: ctaHandler
  } = cta || {};
  const hasCta = ctaText && (ctaHref || ctaHandler);
  const shouldShowCtaSection = actionText || hasCta;
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const shouldShowFeedback = isOpen && popoverIsOpen && feedbackSurveyId !== undefined;
  const {
    onOpenChangeTip,
    onStepChange,
    onTipClick,
    onTurnOffClick
  } = useTipHandlers({
    coachingTipsId,
    tipId,
    setCurrentTipId,
    isOpen,
    dismissCoachingTips
  });
  useEffect(() => {
    if (!isTargetVisible) {
      if (isOpen) {
        setCurrentTipId('');
      }
    }
  }, [isTargetVisible, isOpen, setCurrentTipId]);
  useEffect(() => {
    if (targetElement && isTargetVisible && !tipViewed.current) {
      trackUserInteraction('coaching tip viewed', coachingTipsId, tipId);
      tipViewed.current = true;
    }
  }, [targetElement, isTargetVisible, coachingTipsId, tipId]);
  if (!targetElement || !isTargetVisible) {
    return null;
  }
  return /*#__PURE__*/_jsx(Popover, {
    placement: popoverPlacement || 'top',
    use: "shepherd",
    inlineSize: "390px",
    open: isOpen,
    "data-coaching-tips-popover": tipId,
    "data-test-id": `${tipId}-coaching-tip-popover`,
    onOpenChange: onOpenChangeTip,
    zIndex: popoverZIndex,
    onOpenComplete: () => setPopoverIsOpen(true),
    onCloseComplete: () => setPopoverIsOpen(false),
    content: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(PopoverHeader, {
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h4",
          children: title
        }), /*#__PURE__*/_jsx(PopoverCloseButton, {
          "data-test-id": "coaching-tip-popover-close-button",
          popoverUse: "shepherd",
          onClick: onTipClick
        })]
      }), /*#__PURE__*/_jsxs(PopoverBody, {
        padding: "100",
        children: [/*#__PURE__*/_jsx(CoachingTipSteps, {
          steps: steps,
          onStepChange: onStepChange
        }), shouldShowCtaSection && /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          justifyContent: "center",
          marginBlockEnd: "300",
          "data-test-id": "coaching-tip-cta-section",
          children: [hasCta && /*#__PURE__*/_jsx(Button, {
            "data-test-id": "coaching-tip-cta",
            href: ctaHref,
            onClick: ctaHandler !== null ? ctaHandler : undefined,
            use: "primary",
            children: ctaText
          }), !hasCta && actionText && /*#__PURE__*/_jsx("b", {
            children: actionText
          })]
        })]
      }), /*#__PURE__*/_jsx(PopoverFooter, {
        paddingInline: "0",
        children: /*#__PURE__*/_jsx(BorderTopContainer, {
          children: /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            justifyContent: "center",
            paddingInline: "600",
            paddingBlock: "100",
            children: /*#__PURE__*/_jsx(Text, {
              marginBlockStart: "300",
              typography: "detail-100",
              children: /*#__PURE__*/_jsx(Link, {
                "data-selenium-test": "turn-off-coaching-tips",
                "data-test-id": "coaching-tip-turn-off-hints-cta",
                onClick: onTurnOffClick,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "coachingTips.turnOffHints"
                })
              })
            })
          })
        })
      }), shouldShowFeedback && /*#__PURE__*/_jsx(FloatingFeedback, {
        zIndex: popoverZIndex,
        coachingTipsId: coachingTipsId,
        feedbackSurveyId: feedbackSurveyId,
        tipId: tipId
      })]
    }),
    children: /*#__PURE__*/_jsx(CoachingTipButton, {
      target: targetElement,
      onTipClick: onTipClick,
      onTipRender: onTipRender,
      onTipUnmount: onTipUnmount,
      placement: placement,
      tipId: tipId,
      tipIndex: tipIndex,
      zIndex: zIndex,
      isEmptyTarget: isEmptyTarget
    })
  });
};
export default CoachingTip;